import React, { ChangeEvent } from "react";

import withTitle from "../withTitle";
import { V2 } from "../../Api";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import "../../iframe.css";

interface IEmailOutreachTemplateSelectionViewState {
    isLoading: boolean | string;
    templates: any[];
    resultCount: number;

    settings: any;

    templateId: string;
    surveyId: string;
    subject: string;
}

class EmailOutreachTemplateSelectionView extends React.Component<RouteComponentPropsHistory<any, any>, IEmailOutreachTemplateSelectionViewState> {
    private _iframeRef = React.createRef<HTMLIFrameElement>();

    constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
        super(props);

        const state: IEmailOutreachTemplateSelectionViewState = {
            isLoading: true,
            templates: [],
            resultCount: props.location.state?.resultCount || 0,

            settings: props.location.state?.settings || {},

            templateId: "",
            surveyId: "",
            subject: "",
        };

        if (props.location.state?.excludeIds) {
            state.settings.excludeIds = Object.keys(props.location.state.excludeIds);
        }

        this.state = state;

        this.loadData = this.loadData.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.goBack = this.goBack.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        return (
            <div className="viewer-root full-height input-form">
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h2>Template Selection</h2>
                        </header>

                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-template">Email Template</label>
                                <select id="frm-template" name="templateId" value={this.state.templateId} onChange={e => this.handleInputChanged(e, this.loadAndRenderTemplate)}>
                                    <option value="" />
                                    {this.state.templates.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div className="input-form-group">
                                <label htmlFor="frm-subject">Email Subject</label>
                                <input type="text" id="frm-subject" name="subject" value={this.state.subject} onChange={this.handleInputChanged} />
                            </div>
                        </section>
                    </section>
                    <section>
                        <div>
                            Once you've selected your desired message template, you may continue and review everything.
                        </div>
                        &nbsp;
                        <div className="action-panel-dialog">
                            <button type="button" className="button" onClick={this.goBack}>Back to Results</button>
                            <span className="spring" />
                            {(this.state.templateId && this.state.subject)
                                ? <Link to={{ pathname: "/email-outreach/review", state: { settings: this.state.settings, resultCount: this.state.resultCount, subject: this.state.subject, template: { id: this.state.templateId, name: this.state.templates.find(a => a.id === this.state.templateId)?.name } } }} className="button btn-inline text-with-icon"><span>Review</span><i className="icon-chevron-right" /></Link>
                                : <button type="button" disabled={true} className="button btn-inline text-with-icon"><span>Review</span><i className="icon-chevron-right" /></button>}
                        </div>
                    </section>
                </div>
                <p>Sample output:</p>
                <div className="iframe-holder">
                    <iframe ref={this._iframeRef} title="Template render result" />
                </div>
            </div>
        );
    }

    private async loadData() {
        try {
            const settings = this.state.settings;

            const query = {
                dateStart: settings.dateStart ? DateTime.fromISO(settings.dateStart).startOf("day").toISO() : undefined,
                dateEnd: settings.dateEnd ? DateTime.fromISO(settings.dateEnd).endOf("day").toISO() : undefined,

                ageMin: !parseInt(settings.ageMin) ? undefined : parseInt(settings.ageMin),
                ageMax: !parseInt(settings.ageMax) ? undefined : parseInt(settings.ageMax),

                sex: (settings.sex || null)?.toUpperCase(),

                questionsYes: settings.selectedYesQuestions.map((a: any) => a.id),
                questionsNo: settings.selectedNoQuestions.map((a: any) => a.id),

                fairId: settings.fairId,

                resultFormat: "ids",
                offset: 0,
                limit: 1
            };

            const [results, survey] = await Promise.all([
                V2.get<any>("survey/outreach/email/templates"),
                V2.post<any>("survey/query", query)
            ]);

            this.setState({
                isLoading: false,
                templates: results.templates,
                surveyId: survey.results[0]
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not execute request"
            });
        }
    }

    private goBack() {
        this.props.history.goBack();
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, callback?: () => void) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            [name]: value
        } as any, callback);
    }

    private async loadAndRenderTemplate() {
        if (!this.state.templateId) {
            return;
        }

        try {
            const parameters = [
                `template=${this.state.templateId}`,
                `survey=${this.state.surveyId}`,
            ];

            this.setState({
                subject: this.state.templates.find(a => a.id === this.state.templateId)?.title || ""
            });

            const result = await V2.get<any>(`survey/outreach/email/render-template?${parameters.join("&")}`);

            if (!this._iframeRef.current || !this._iframeRef.current.contentWindow) {
                return;
            }

            this._iframeRef.current.contentWindow.document.body.innerHTML = result.result;
        }
        catch (e) {
            if (this._iframeRef.current?.contentWindow) {
                this._iframeRef.current.contentWindow.document.body.innerHTML = e.message || "Could not load template";
            }
        }
    }
}

export default withTitle("Email Outreach Template Selection", EmailOutreachTemplateSelectionView);